/**
 * 사이드바 네비게이션 컴포넌트
 * 
 * 제휴사 Admin 구조를 참고하여 본사 Admin에 맞게 구현
 * 
 * 작성일: 2025-12-01
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { getMenus } from '../../services/menuService';
import { getAdminMenuPermission } from '../../services/menuPermissionService';
import type { Menu } from '../../types/menu';
import { getIcon } from '../../utils/iconLoader';
import { IconType } from 'react-icons';
import './Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MenuItemWithPermission extends Menu {
  hasPermission?: boolean;
  iconComponent?: IconType | null;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const [menusWithPermissions, setMenusWithPermissions] = useState<MenuItemWithPermission[]>([]);

  // 메뉴 목록 조회 (활성화된 메뉴만)
  // queryKey를 ['menus']로 통일하여 메뉴 관리 페이지에서 무효화 시 자동 갱신되도록 함
  // 필터링 조건(is_active: true)은 queryFn 내부에서 처리
  const { data: menusData, isLoading: menusLoading } = useQuery({
    queryKey: ['menus'],
    queryFn: () => getMenus({ is_active: true }),
    enabled: !!user, // 관리자 정보가 있을 때만 조회
  });

  // 각 메뉴에 대한 권한 확인 및 아이콘 로드
  useEffect(() => {
    if (!menusData?.data?.menus || !user || !user.id) {
      setMenusWithPermissions([]);
      return;
    }

    const processMenus = async () => {
      const menus: Menu[] = menusData.data.menus;
      
      // 모든 메뉴를 평면화하여 권한 확인을 병렬로 처리
      const flattenMenus = (menuList: Menu[]): Menu[] => {
        const result: Menu[] = [];
        menuList.forEach((menu) => {
          result.push(menu);
          if (menu.children && menu.children.length > 0) {
            result.push(...flattenMenus(menu.children));
          }
        });
        return result;
      };

      const allMenus = flattenMenus(menus);
      
      // 모든 메뉴의 권한을 병렬로 확인
      const permissionPromises = allMenus.map(async (menu) => {
        let hasPermission = true; // 기본값을 true로 설정
        try {
          if (user?.id && menu.id) {
            const permissionData = await getAdminMenuPermission(user.id, menu.id);
            hasPermission = permissionData.data?.permission?.can_read ?? true;
          }
        } catch (error) {
          // 권한 확인 실패 시 기본적으로 표시 (API 에러 시에도 메뉴 표시)
          console.warn(`메뉴 권한 확인 실패 (menu_id: ${menu.id}):`, error);
          hasPermission = true; // 에러 시에도 메뉴 표시
        }
        return { menu, hasPermission };
      });

      const permissionResults = await Promise.all(permissionPromises);
      
      // 권한 결과를 맵으로 변환
      const permissionMap = new Map<number, boolean>();
      permissionResults.forEach(({ menu, hasPermission }) => {
        permissionMap.set(menu.id, hasPermission);
      });

      // 계층 구조를 유지하면서 권한 필터링 및 아이콘 로드
      const processMenu = (menu: Menu): MenuItemWithPermission | null => {
        const hasPermission = permissionMap.get(menu.id) ?? true;
        
        if (!hasPermission) {
          return null;
        }

        const iconComponent = getIcon(menu.icon);
        const processedMenu: MenuItemWithPermission = {
          ...menu,
          iconComponent,
          hasPermission: true,
        };

        // 하위 메뉴 처리
        if (menu.children && menu.children.length > 0) {
          const processedChildren = menu.children
            .map((child) => processMenu(child))
            .filter((child) => child !== null) as MenuItemWithPermission[];
          processedMenu.children = processedChildren;
        }

        return processedMenu;
      };

      // 모든 메뉴 처리
      const processedMenus = menus
        .map((menu) => processMenu(menu))
        .filter((menu) => menu !== null) as MenuItemWithPermission[];

      setMenusWithPermissions(processedMenus);
    };

    processMenus();
  }, [menusData, user]);

  // 섹션별로 그룹화
  const visibleMenus = useMemo(() => {
    if (!menusWithPermissions || menusWithPermissions.length === 0) {
      return [];
    }

    const sections: { [key: string]: MenuItemWithPermission[] } = {};
    
    menusWithPermissions.forEach((menu) => {
      if (!sections[menu.section]) {
        sections[menu.section] = [];
      }
      sections[menu.section].push(menu);
    });

    return Object.entries(sections).map(([section, items]) => ({
      section,
      items,
    }));
  }, [menusWithPermissions]);

  const [expandedMenus, setExpandedMenus] = useState<Set<number>>(new Set());

  // 현재 경로에 해당하는 부모 메뉴들을 자동으로 확장
  useEffect(() => {
    if (!menusWithPermissions || !location.pathname) return;

    const findParentMenus = (menus: MenuItemWithPermission[], targetPath: string, parents: number[] = []): number[] => {
      for (const menu of menus) {
        if (menu.path === targetPath || targetPath.startsWith(menu.path + '/')) {
          return [...parents, menu.id];
        }
        if (menu.children && menu.children.length > 0) {
          const found = findParentMenus(menu.children, targetPath, [...parents, menu.id]);
          if (found.length > 0) {
            return found;
          }
        }
      }
      return [];
    };

    const parentIds = findParentMenus(menusWithPermissions, location.pathname);
    setExpandedMenus(new Set(parentIds));
  }, [menusWithPermissions, location.pathname]);

  const toggleMenu = (menuId: number) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const hasActiveChild = (menu: MenuItemWithPermission): boolean => {
    if (!menu.children || menu.children.length === 0) {
      return false;
    }
    return menu.children.some((child) => isActive(child.path) || hasActiveChild(child));
  };

  // 재귀적으로 메뉴 아이템 렌더링
  const renderMenuItem = (item: MenuItemWithPermission, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.id);
    const isItemActive = isActive(item.path);
    const hasActive = hasActiveChild(item);
    const itemClassName = `sidebar-item ${isItemActive ? 'active' : ''} ${hasActive ? 'has-active-child' : ''} ${level > 0 ? 'sidebar-item-nested' : ''}`;
    const itemStyle = { paddingLeft: `${1.5 + level * 1}rem` };

    const menuContent = (
      <>
        {hasChildren && (
          <span className="sidebar-expand-icon">
            {isExpanded ? '▼' : '▶'}
          </span>
        )}
        {!hasChildren && level > 0 && (
          <span className="sidebar-expand-icon sidebar-expand-icon-placeholder">•</span>
        )}
        {item.iconComponent && (
          <span className="sidebar-icon">
            {React.createElement(item.iconComponent as React.ComponentType<any>)}
          </span>
        )}
        <span className="sidebar-label">{item.label}</span>
      </>
    );

    return (
      <React.Fragment key={item.id}>
        <div className={`sidebar-item-wrapper ${level > 0 ? 'sidebar-item-child' : ''}`}>
          {hasChildren ? (
            <Nav.Link
              as={Link}
              to={item.path}
              className={itemClassName}
              onClick={() => {
                // 부모 메뉴 클릭 시 하위 메뉴도 펼치기
                if (!isExpanded) {
                  toggleMenu(item.id);
                }
                onClose();
              }}
              style={itemStyle}
            >
              {menuContent}
            </Nav.Link>
          ) : (
            <Nav.Link
              as={Link}
              to={item.path}
              className={itemClassName}
              onClick={onClose}
              style={itemStyle}
            >
              {menuContent}
            </Nav.Link>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="sidebar-submenu">
            {item.children?.map((child) => renderMenuItem(child, level + 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  const isLoading = authLoading || menusLoading;

  return (
    <>
      {/* 모바일 오버레이 */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      {/* 사이드바 */}
      <aside className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <Nav className="sidebar-nav" as="nav">
          {isLoading ? (
            <div className="sidebar-empty">
              <span>로딩 중...</span>
            </div>
          ) : visibleMenus.length === 0 ? (
            <div className="sidebar-empty">
              <span>메뉴 항목이 없습니다.</span>
            </div>
          ) : (
            visibleMenus.map(({ section, items }) => (
              <div key={section} className="sidebar-section">
                <div className="sidebar-section-header">
                  {section}
                </div>
                {items.map((item) => renderMenuItem(item, 0))}
              </div>
            ))
          )}
        </Nav>
      </aside>
    </>
  );
};

export default Sidebar;
